/**
 * extractCtes — token-aware CTE extractor for the WITH clause of a SELECT (or
 * any DML) statement. Pure function; uses `tokenSkip` so dialect-specific
 * comments / strings / quoted identifiers (backticks, brackets, dollar tags)
 * are skipped inside paren bodies (VQE-RO9 — no regex parsing).
 *
 * Returns one binding per CTE in declaration order, each carrying its body
 * range, optional explicit column list, the body's first-keyword kind, and a
 * per-binding `isRecursive` flag. Per-binding recursion is REFERENCE-based
 * (decision 4): a binding is recursive iff the engine's CTE self-visibility
 * holds (`cteSelfVisibility`) AND its body self-references in a relation slot
 * (`detectCteSelfReference`). The clause-level `CteOutline.isRecursive` stays
 * keyword-derived (true when `WITH RECURSIVE` is present). `detectRecursiveBranchShape`
 * classifies the standard anchor/recursive-step `UNION [ALL]` shape for branch
 * labelling (decision 6). The per-binding scan runs once per binding per
 * extraction — callers memoise `extractCtes` (SelectRenderer's `cteOutline`
 * useMemo), so there is no per-render rescan.
 */
import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { skipContextFor } from '../../../../sql-resolver/classify/tokenSkip';
import { cteSelfVisibility, detectCteSelfReference } from '../../../../sql-resolver/scope/cteRecursion';
import {
  IDENT_CONT,
  IDENT_START,
  consumeParenGroup,
  readColumnList,
  readWord,
  skipWsAndComments,
} from '../insert/insertParseHelpers';
import { extractSetOperatorBranches, type SetOperatorOutline } from './extractSetOperatorBranches';

export type CteBodyKind = 'select' | 'insert' | 'update' | 'delete' | 'merge' | 'unknown';

export interface CteBinding {
  readonly name: string;
  readonly nameStart: number;
  readonly nameEnd: number;
  readonly columns: readonly string[] | null;
  readonly columnsStart: number | null;
  readonly columnsEnd: number | null;
  readonly bodyStart: number;
  readonly bodyEnd: number;
  readonly bodyKind: CteBodyKind;
  readonly isRecursive: boolean;
  readonly definitionStart: number;
  readonly definitionEnd: number;
}

export interface CteOutline {
  readonly hasWith: boolean;
  readonly isRecursive: boolean;
  readonly withKeywordStart: number | null;
  readonly recursiveKeywordEnd: number | null;
  readonly mainStatementStart: number;
  readonly bindings: readonly CteBinding[];
}

export function extractCtes(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): CteOutline {
  const start = Math.max(0, statementRange.startOffset);
  const end = Math.max(start, Math.min(source.length, statementRange.endOffset));
  const ctx = skipContextFor(options);
  const empty: CteOutline = {
    hasWith: false,
    isRecursive: false,
    withKeywordStart: null,
    recursiveKeywordEnd: null,
    mainStatementStart: start,
    bindings: [],
  };

  const i = skipWsAndComments(source, start, end);
  const withWord = readWord(source, i, end);
  if (!withWord || withWord.upper !== 'WITH') return empty;

  let cursor = withWord.end;
  cursor = skipWsAndComments(source, cursor, end);
  let isRecursive = false;
  let recursiveEnd: number | null = null;
  const recursiveProbe = readWord(source, cursor, end);
  if (recursiveProbe && recursiveProbe.upper === 'RECURSIVE') {
    isRecursive = true;
    recursiveEnd = recursiveProbe.end;
    cursor = recursiveProbe.end;
  }

  const bindings: CteBinding[] = [];
  while (cursor < end) {
    cursor = skipWsAndComments(source, cursor, end);
    if (cursor >= end) break;
    const binding = parseCteBinding(source, cursor, end, ctx, options, isRecursive);
    if (!binding) break;
    bindings.push(binding);
    cursor = binding.definitionEnd;
    cursor = skipWsAndComments(source, cursor, end);
    if (cursor < end && source[cursor] === ',') {
      cursor++;
      continue;
    }
    break;
  }

  const mainStart = skipWsAndComments(source, cursor, end);
  return {
    hasWith: true,
    isRecursive,
    withKeywordStart: withWord.start,
    recursiveKeywordEnd: recursiveEnd,
    mainStatementStart: bindings.length === 0 ? withWord.end : mainStart,
    bindings,
  };
}

/**
 * Classify a recursion-badged CTE body as the STANDARD anchor/recursive-step
 * shape (decision 6): exactly two branches joined by `UNION [ALL]`, with the
 * self-reference in branch 2 (the recursive step) and NOT in branch 1 (the
 * anchor). Returns the two-branch `SetOperatorOutline` for the caller to render
 * with "Anchor query" / "Recursive step" labels, or `null` for any other shape
 * (non-recursive binding, ≥3 branches, INTERSECT/EXCEPT/MINUS, self-ref in the
 * anchor) — those fall back to a single unlabelled body.
 *
 * Pure; safe to call per render but the caller memoises it alongside the body
 * bounds. Requires `binding.isRecursive` (reference-based) as the precondition.
 */
export function detectRecursiveBranchShape(
  source: string,
  binding: CteBinding,
  options: ParseOptions,
): SetOperatorOutline | null {
  if (!binding.isRecursive) return null;
  const bodyRange: SqlRange = {
    startOffset: binding.bodyStart,
    endOffset: binding.bodyEnd,
    startLine: 0,
    startColumn: 0,
    endLine: 0,
    endColumn: 0,
  };
  const outline = extractSetOperatorBranches(source, bodyRange, options);
  if (!outline.hasSetOperators || outline.branches.length !== 2) return null;
  const separator = outline.separators[0];
  if (!separator || separator.kind !== 'UNION') return null;

  const cteNameQuoted = isQuotedNameChar(source[binding.nameStart]);
  const selfRefIn = (branch: { start: number; end: number }): boolean =>
    detectCteSelfReference({
      source,
      options,
      bodyStart: branch.start,
      bodyEnd: branch.end,
      cteName: binding.name,
      cteNameQuoted,
    });
  // Anchor (branch 1) must be non-recursive; the recursive step (branch 2) must
  // self-reference. Any other split (self-ref in branch 1, or in neither) is not
  // the standard shape.
  if (selfRefIn(outline.branches[0]!) || !selfRefIn(outline.branches[1]!)) return null;
  return outline;
}

function parseCteBinding(
  source: string,
  cursor: number,
  end: number,
  ctx: SkipContext,
  options: ParseOptions,
  clauseIsRecursive: boolean,
): CteBinding | null {
  const definitionStart = cursor;
  const name = readIdentifier(source, cursor, end);
  if (!name) return null;
  const nameQuoted = isQuotedNameChar(source[name.start]);
  let i = name.end;
  i = skipWsAndComments(source, i, end);

  let columns: string[] | null = null;
  let columnsStart: number | null = null;
  let columnsEnd: number | null = null;
  if (i < end && source[i] === '(') {
    columnsStart = i;
    const groupEnd = consumeParenGroup(source, i, end, ctx);
    columnsEnd = groupEnd;
    columns = readColumnList(source, i + 1, groupEnd - 1, ctx);
    i = groupEnd;
    i = skipWsAndComments(source, i, end);
  }

  const asWord = readWord(source, i, end);
  if (!asWord || asWord.upper !== 'AS') return null;
  i = asWord.end;
  i = skipWsAndComments(source, i, end);

  // Optional NOT MATERIALIZED / MATERIALIZED (PG); consumed but not surfaced.
  const probe = readWord(source, i, end);
  if (probe && probe.upper === 'NOT') {
    const after = skipWsAndComments(source, probe.end, end);
    const next = readWord(source, after, end);
    if (next && next.upper === 'MATERIALIZED') {
      i = skipWsAndComments(source, next.end, end);
    }
  } else if (probe && probe.upper === 'MATERIALIZED') {
    i = skipWsAndComments(source, probe.end, end);
  }

  if (i >= end || source[i] !== '(') return null;
  const bodyOpenParen = i;
  const bodyCloseParenExclusive = consumeParenGroup(source, bodyOpenParen, end, ctx);
  if (bodyCloseParenExclusive <= bodyOpenParen + 1) return null;
  const bodyStart = bodyOpenParen + 1;
  const bodyEnd = bodyCloseParenExclusive - 1;
  const bodyKind = detectBodyKind(source, bodyStart, bodyEnd);

  // Reference-based per-binding recursion (decision 4): the engine must permit a
  // CTE self-reference here (keyword-less on mssql/oracle; RECURSIVE-gated on
  // pg/mysql/sqlite — `cteSelfVisibility`) AND the body must self-reference in a
  // relation slot (`detectCteSelfReference`). This runs once per binding per
  // `extractCtes` call, which callers memoise — no per-render rescan.
  const isRecursive =
    cteSelfVisibility(options.engine, clauseIsRecursive) &&
    detectCteSelfReference({
      source,
      options,
      bodyStart,
      bodyEnd,
      cteName: name.value,
      cteNameQuoted: nameQuoted,
    });

  return {
    name: name.value,
    nameStart: name.start,
    nameEnd: name.end,
    columns,
    columnsStart,
    columnsEnd,
    bodyStart,
    bodyEnd,
    bodyKind,
    isRecursive,
    definitionStart,
    definitionEnd: bodyCloseParenExclusive,
  };
}

// A CTE name is "quoted" when it opens with a double quote, backtick, or an
// MSSQL bracket — the same three quoting styles `readIdentifier` strips.
function isQuotedNameChar(c: string | undefined): boolean {
  return c === '"' || c === '`' || c === '[';
}

interface IdentifierMatch {
  readonly value: string;
  readonly start: number;
  readonly end: number;
}

function readIdentifier(source: string, from: number, end: number): IdentifierMatch | null {
  if (from >= end) return null;
  const c = source[from];
  if (c === '"') {
    let j = from + 1;
    while (j < end && source[j] !== '"') j++;
    if (j >= end) return null;
    return { value: source.slice(from + 1, j).replace(/""/g, '"'), start: from, end: j + 1 };
  }
  if (c === '`') {
    let j = from + 1;
    while (j < end && source[j] !== '`') j++;
    if (j >= end) return null;
    return { value: source.slice(from + 1, j), start: from, end: j + 1 };
  }
  if (c === '[') {
    let j = from + 1;
    while (j < end && source[j] !== ']') j++;
    if (j >= end) return null;
    return { value: source.slice(from + 1, j), start: from, end: j + 1 };
  }
  if (!IDENT_START.test(c)) return null;
  let j = from + 1;
  while (j < end && IDENT_CONT.test(source[j])) j++;
  return { value: source.slice(from, j), start: from, end: j };
}

function detectBodyKind(source: string, start: number, end: number): CteBodyKind {
  const i = skipWsAndComments(source, start, end);
  const w = readWord(source, i, end);
  if (!w) return 'unknown';
  switch (w.upper) {
    case 'SELECT':
    case 'WITH': // recursive WITH at the body level — treat the inner SELECT as a SELECT
      return 'select';
    case 'INSERT': return 'insert';
    case 'UPDATE': return 'update';
    case 'DELETE': return 'delete';
    case 'MERGE': return 'merge';
    case 'VALUES': return 'select'; // VALUES-only CTE renders as a degenerate select
    default: return 'unknown';
  }
}
